import { normalizeContentEditorLanguage } from "./contentEditorHelpers";

/** Languages the composer can offer a course. */
export const COMPOSER_AVAILABLE_LANGUAGES = [
  { value: "english", label: "English" },
  { value: "hebrew", label: "Hebrew" },
];

export const normalizeLanguageKey = normalizeContentEditorLanguage;

export const formatLanguageName = (lang) => {
  const found = COMPOSER_AVAILABLE_LANGUAGES.find(
    (l) => l.value === normalizeLanguageKey(lang)
  );
  if (found) return found.label;
  const s = String(lang || "");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * The language directive appended to every AI refinement prompt.
 *
 * Eight editors carried a byte-identical private copy of this. Pass a
 * `sourceLanguage` to ask for a translation *from* it; leave it null to ask the
 * model to stay in `targetLanguage`.
 */
export const buildLanguageInstructions = (
  targetLanguage,
  sourceLanguage = null,
  baseInstructions = ""
) => {
  const targetName = formatLanguageName(targetLanguage);
  const sourceName = sourceLanguage ? formatLanguageName(sourceLanguage) : "";
  const directive = sourceLanguage
    ? `Translate all returned fields from ${sourceName} to ${targetName}. Return only ${targetName} text for every field.`
    : `Keep all returned fields in ${targetName}. Return only ${targetName} text for every field.`;
  const trimmed = (baseInstructions || "").trim();
  return trimmed ? `${trimmed}\n\n${directive}` : directive;
};

/** A short 2-letter code for compact per-language status chips (english -> EN, hebrew -> HE). */
export const getShortLanguageCode = (lang) => {
  const normalized = normalizeLanguageKey(lang);
  const known = { english: "EN", hebrew: "HE" };
  if (known[normalized]) return known[normalized];
  return String(normalized || lang || "").slice(0, 2).toUpperCase();
};

export const normalizeSupportedLanguages = (langs) => {
  const normalized = Array.from(
    new Set(
      (Array.isArray(langs) ? langs : [])
        .map((lang) => normalizeLanguageKey(lang))
        .filter(Boolean)
    )
  );
  return normalized.length > 0 ? normalized : ["english"];
};

export const getCourseSupportedLanguages = (course) =>
  normalizeSupportedLanguages(course?.supported_languages);

/** Selected language if the course supports it, else the course's first (authoring-order) language. */
export const clampComposerLanguage = (lang, supportedLanguages) => {
  const supported = normalizeSupportedLanguages(supportedLanguages);
  const normalized = normalizeLanguageKey(lang);
  if (normalized && supported.includes(normalized)) return normalized;
  return supported[0];
};

/**
 * Why an activation switch that acts on the Composer's language must wait, or "" once it may act.
 * Until the item's course languages and the Composer's own course have both loaded, the language
 * clamp falls back to English, and the switch would act on English for an author working in Hebrew.
 *
 * @param {"loading"|"loaded"|"failed"} courseLanguagesStatus - the editor's lookup of its course's languages
 * @param {boolean} composerLanguageReady - the Composer shell has loaded its course
 */
export const activationLanguageWaitReason = (courseLanguagesStatus, composerLanguageReady) => {
  if (courseLanguagesStatus === "failed") {
    return "The course's languages didn't load. Reload the page to set activation status.";
  }
  if (courseLanguagesStatus !== "loaded" || !composerLanguageReady) {
    return "Loading the course's languages…";
  }
  return "";
};

const COMPOSER_LANGUAGE_KEY = "composerDisplayLanguage";

const getComposerLanguageKey = (courseId) => {
  if (courseId == null || courseId === "") return COMPOSER_LANGUAGE_KEY;
  return `${COMPOSER_LANGUAGE_KEY}:${courseId}`;
};

export const readStoredComposerLanguage = (courseId) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(getComposerLanguageKey(courseId));
    return normalizeLanguageKey(raw) || null;
  } catch (_err) {
    return null;
  }
};

export const writeStoredComposerLanguage = (courseId, lang) => {
  if (typeof window === "undefined") return;
  try {
    const normalized = normalizeLanguageKey(lang);
    if (!normalized) return;
    sessionStorage.setItem(getComposerLanguageKey(courseId), normalized);
  } catch (_err) {
    // Ignore storage errors (private mode, quota exceeded, etc.)
  }
};
